import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Check, ThumbsUp, Flame, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import SiteNav from "@/components/SiteNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/tf9we5d6_hamara8-220.jpg";
const EVENT_LOGO = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";
const PLACEHOLDER_FIGHTER = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";

function FlameBar({ votes, totalVotes }) {
  const pct = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <Flame className={`w-3 h-3 shrink-0 ${votes > 0 ? 'text-[#FF3B30]' : 'text-white/10'}`} />
      <div className="flex-1 h-1.5 bg-white/5 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#FF3B30] to-[#FF6B00] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function VoteCard({ name, image_url, subtitle, selected, onClick, votes, totalVotes, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`bg-[#121212] border p-3 transition-all duration-200 text-center ${selected ? 'border-white bg-white/10' : disabled ? 'border-white/5 opacity-60 cursor-default' : 'border-white/10 hover:border-white/30 hover:bg-white/5'}`}
      data-testid={`vote-option-${name}`}>
      <div className={`w-16 h-16 mx-auto mb-2 overflow-hidden rounded-full ${selected ? 'ring-2 ring-white' : ''}`}>
        <img src={image_url || PLACEHOLDER_FIGHTER} alt={name} className={`w-full h-full ${image_url ? "object-cover object-top grayscale" : "object-contain p-2 opacity-30"}`} />
      </div>
      <p className="text-xs lg:text-sm uppercase tracking-tight leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{name}</p>
      {subtitle && <p className="text-[8px] lg:text-[9px] text-[#A1A1AA] uppercase tracking-wider">{subtitle}</p>}
      {selected && <Check className="w-4 h-4 text-white mx-auto mt-1" />}
      <FlameBar votes={votes} totalVotes={totalVotes} />
    </button>
  );
}

function MatchupCard({ fight, selected, onClick, votes, totalVotes, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`bg-[#121212] border p-3 transition-all duration-200 ${selected ? 'border-white bg-white/10' : disabled ? 'border-white/5 opacity-60 cursor-default' : 'border-white/10 hover:border-white/30 hover:bg-white/5'}`}
      data-testid={`vote-matchup-${fight.id}`}>
      <div className="flex items-center gap-2 justify-center">
        <div className="w-10 h-10 overflow-hidden rounded-full shrink-0">
          <img src={fight.fighter1.image_url || PLACEHOLDER_FIGHTER} alt={fight.fighter1.name} className={`w-full h-full ${fight.fighter1.image_url ? "object-cover object-top grayscale" : "object-contain p-2 opacity-30"}`} />
        </div>
        <div className="text-center">
          <p className="text-xs lg:text-sm uppercase tracking-tight leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{fight.fighter1.name}</p>
          <p className="text-[9px] text-white/30">vs</p>
          <p className="text-xs lg:text-sm uppercase tracking-tight leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{fight.fighter2.name}</p>
        </div>
        <div className="w-10 h-10 overflow-hidden rounded-full shrink-0">
          <img src={fight.fighter2.image_url || PLACEHOLDER_FIGHTER} alt={fight.fighter2.name} className={`w-full h-full ${fight.fighter2.image_url ? "object-cover object-top grayscale" : "object-contain p-2 opacity-30"}`} />
        </div>
      </div>
      {fight.fight_type && <p className="text-[8px] text-[#A1A1AA] uppercase tracking-wider text-center mt-1">{fight.fight_type}</p>}
      {selected && <Check className="w-4 h-4 text-white mx-auto mt-1" />}
      <FlameBar votes={votes} totalVotes={totalVotes} />
    </button>
  );
}

export default function VotePage({ votePicks, setVotePicks }) {
  const [fights, setFights] = useState([]);
  const [questions, setQuestions] = useState({ best_fight: "Paras ottelu", best_fighter: "Kovin ottelija", surprise: "Ablemans: Illan yllättäjä" });
  const [tallies, setTallies] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [siteTexts, setSiteTexts] = useState({ vote_hero_description: "Valitse suosikkisi ja paina äänestä!", vote_marketing_checkbox: "Haluan osallistua lippupaketin arvontaan Hamara vol. 10 juhlatapahtumaan" });

  const fetchTallies = useCallback(async () => {
    try { const res = await axios.get(`${API}/votes/tally-public`); setTallies(res.data); } catch (err) { console.error("Failed to fetch tallies:", err); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/fights`).then(res => setFights(res.data)).catch(err => console.error("Failed to load fights:", err));
    axios.get(`${API}/vote-questions`).then(res => setQuestions(prev => ({ ...prev, ...res.data.questions }))).catch(err => console.error("Failed to load vote questions:", err));
    axios.get(`${API}/site-content`).then(res => setSiteTexts(prev => ({ ...prev, ...res.data.texts }))).catch(err => console.error("Failed to load site content:", err));
    fetchTallies();
  }, [fetchTallies]);

  const allFighters = useMemo(() => {
    const fighters = [];
    fights.forEach(f => { if (f.fighter1?.name) fighters.push(f.fighter1); if (f.fighter2?.name) fighters.push(f.fighter2); });
    return fighters;
  }, [fights]);

  const getTotalVotes = (category) => {
    const catTallies = tallies[category] || {};
    return Object.values(catTallies).reduce((sum, v) => sum + v, 0);
  };

  const handlePick = (category, value) => {
    if (submitted) return;
    setTallies(prev => {
      const updated = { ...prev };
      if (votePicks[category] && updated[category]?.[votePicks[category]]) {
        updated[category] = { ...updated[category] };
        updated[category][votePicks[category]] = Math.max(0, updated[category][votePicks[category]] - 1);
      }
      if (!updated[category]) updated[category] = {};
      updated[category] = { ...updated[category] };
      updated[category][value] = (updated[category][value] || 0) + 1;
      return updated;
    });
    setVotePicks(prev => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const votePromises = Object.entries(votePicks).map(([category, value]) => axios.post(`${API}/votes`, { category, value }));
      if (email) {
        votePromises.push(axios.post(`${API}/vote-email`, { email, marketing_consent: marketingConsent }).catch(err => {
          if (!err.response?.data?.detail?.includes("jo rekisteröity")) throw err;
        }));
      }
      await Promise.all(votePromises);
      setSubmitted(true);
      toast.success("Äänet rekisteröity!");
    } catch { toast.error("Äänestys epäonnistui"); }
    finally { setSubmitting(false); }
  };

  const hasAnyPick = Object.keys(votePicks).length > 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero with Nav */}
      <section className="relative py-10 sm:py-14 flex flex-col items-center justify-center overflow-hidden"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: '30% 25%' }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 75%, rgba(10,10,10,1) 100%)' }} />
        <div className="relative z-10 text-center px-4">
          <Link to="/"><img src={EVENT_LOGO} alt="Hamara MMA" className="h-10 sm:h-14 mx-auto mb-4 object-contain" /></Link>
          <p className="text-white/50 text-xs lg:text-sm">{siteTexts.vote_hero_description}</p>
          <SiteNav />
        </div>
      </section>

      {/* Best Fight */}
      <section className="py-8 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6"><div className="h-px flex-1 bg-white/10" />
            <h2 className="text-2xl sm:text-3xl uppercase tracking-tight text-center" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{questions.best_fight}</h2>
            <div className="h-px flex-1 bg-white/10" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vote-best-fight">
            {fights.map(fight => {
              const label = `${fight.fighter1.name} vs ${fight.fighter2.name}`;
              return <MatchupCard key={fight.id} fight={fight} selected={votePicks.best_fight === label}
                onClick={() => handlePick("best_fight", label)} votes={tallies.best_fight?.[label] || 0}
                totalVotes={getTotalVotes("best_fight")} disabled={submitted} />;
            })}
          </div>
        </div>
      </section>

      {/* Best Fighter */}
      <section className="py-8 px-4 md:px-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6"><div className="h-px flex-1 bg-white/10" />
            <h2 className="text-2xl sm:text-3xl uppercase tracking-tight text-center" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{questions.best_fighter}</h2>
            <div className="h-px flex-1 bg-white/10" /></div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="vote-best-fighter">
            {allFighters.map(f => <VoteCard key={f.name} name={f.name} image_url={f.image_url} subtitle={f.hometown}
              selected={votePicks.best_fighter === f.name} onClick={() => handlePick("best_fighter", f.name)}
              votes={tallies.best_fighter?.[f.name] || 0} totalVotes={getTotalVotes("best_fighter")} disabled={submitted} />)}
          </div>
        </div>
      </section>

      {/* Surprise */}
      <section className="py-8 px-4 md:px-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6"><div className="h-px flex-1 bg-white/10" />
            <h2 className="text-2xl sm:text-3xl uppercase tracking-tight text-center" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{questions.surprise}</h2>
            <div className="h-px flex-1 bg-white/10" /></div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="vote-surprise">
            {allFighters.map(f => <VoteCard key={f.name} name={f.name} image_url={f.image_url} subtitle={f.hometown}
              selected={votePicks.surprise === f.name} onClick={() => handlePick("surprise", f.name)}
              votes={tallies.surprise?.[f.name] || 0} totalVotes={getTotalVotes("surprise")} disabled={submitted} />)}
          </div>
        </div>
      </section>

      {/* Submit */}
      <section className="py-10 px-4 md:px-8 border-t border-white/10">
        <div className="max-w-xl mx-auto">
          {submitted ? (
            <div className="text-center">
              <ThumbsUp className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
              <p className="text-white text-sm">Kiitos äänestäsi!</p>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input type="email" placeholder="Sähköpostiosoite (vapaaehtoinen)" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent border-0 border-b-2 border-white/20 text-white focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#FF3B30] transition-colors py-3 pl-8 placeholder:text-white/40 text-lg rounded-none" data-testid="vote-email-input" />
              </div>
              <div className="text-left bg-[#121212] border border-white/10 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox id="vote-marketing" checked={marketingConsent} onCheckedChange={setMarketingConsent}
                    className="mt-0.5 border-white/30 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]" data-testid="vote-marketing-checkbox" />
                  <Label htmlFor="vote-marketing" className="text-sm cursor-pointer leading-relaxed text-white">
                    {siteTexts.vote_marketing_checkbox}
                  </Label>
                </div>
              </div>
              <div className="text-center">
                <Button type="submit" disabled={!hasAnyPick || submitting}
                  className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-10 py-5 transition-colors duration-200 shadow-[0_0_15px_rgba(255,59,48,0.3)] hover:shadow-[0_0_25px_rgba(255,59,48,0.5)] rounded-none disabled:opacity-50 disabled:cursor-not-allowed btn-shine" data-testid="vote-submit-btn">
                  {submitting ? "Lähetetään..." : "Äänestä"}<ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors"
              data-testid="scroll-to-top">
              <ChevronRight className="w-5 h-5 -rotate-90" />
            </button>
            <span className="text-xs text-[#A1A1AA]">Hamara MMA Vol 9</span>
          </div>
          <Link to="/admin" className="text-xs text-white/30 hover:text-white/50 transition-colors">Admin</Link>
        </div>
      </footer>
    </div>
  );
}
